import { CircleAlert, MailCheck } from "lucide-react";
import { useMemo, useRef, useState, type FormEvent } from "react";
import { Button } from "../../../design/components/Button.js";
import { ResponsiveSheet } from "../../../design/components/ResponsiveSheet.js";
import { api, type Conference } from "../../api.js";
import { apiErrorMessage } from "../../../api/client.js";

function dateValue(offsetDays: number) {
  const value = new Date();
  value.setUTCDate(value.getUTCDate() + offsetDays);
  return value.toISOString().slice(0, 10);
}

function commandKey() {
  return globalThis.crypto?.randomUUID?.() ?? `reviewer-bulk-${Date.now()}-${Math.random()}`;
}

function parsedAddresses(value: string) {
  const unique = new Set<string>();
  for (const part of value.split(/[\s,;]+/)) {
    const email = part.trim().toLowerCase();
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) unique.add(email);
  }
  return [...unique];
}

export function AddReviewersSheet({ conference, open, onClose, onCompleted }: {
  conference: Conference;
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
}) {
  const [rawAddresses, setRawAddresses] = useState("");
  const [opensOn, setOpensOn] = useState(() => dateValue(0));
  const [closesOn, setClosesOn] = useState(() => dateValue(14));
  const [expectedWork, setExpectedWork] = useState("2");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [failed, setFailed] = useState(false);
  const keyRef = useRef(commandKey());
  const addresses = useMemo(() => parsedAddresses(rawAddresses), [rawAddresses]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !addresses.length || addresses.length > 200) return;
    setBusy(true);
    setMessage("");
    setFailed(false);
    try {
      const result = await api.bulkInviteReviewers(
        conference.id,
        conference.organization_id,
        {
          records: addresses.map((email) => ({ email })),
          opens_at: `${opensOn}T00:00:00.000Z`,
          closes_at: `${closesOn}T23:59:59.000Z`,
          expected_work: Number(expectedWork),
        },
        keyRef.current,
      );
      const queued = result.summary.queued ?? 0;
      const duplicate = result.summary.duplicate ?? 0;
      const invalid = result.summary.invalid ?? 0;
      setMessage(`${queued} invitations queued${duplicate ? ` · ${duplicate} already invited` : ""}${invalid ? ` · ${invalid} invalid` : ""}.`);
      keyRef.current = commandKey();
      onCompleted();
    } catch (reason) {
      setFailed(true);
      setMessage(apiErrorMessage(reason, "Invitations could not be queued. Your addresses are still here."));
      keyRef.current = commandKey();
    } finally {
      setBusy(false);
    }
  };

  const buttonLabel = addresses.length ? `Send ${addresses.length} invitation${addresses.length === 1 ? "" : "s"}` : "Send invitations";

  return <ResponsiveSheet
    open={open}
    title="Add reviewers"
    onClose={onClose}
    footer={<div className="preview-sheet-actions"><Button variant="secondary" type="button" onClick={onClose} disabled={busy}>Close</Button><Button type="submit" form="bulk-reviewer-form" busy={busy} disabled={!addresses.length || addresses.length > 200}>{buttonLabel}</Button></div>}
  >
    <form id="bulk-reviewer-form" className="bulk-reviewer-form" onSubmit={submit}>
      <div><strong>{conference.name}</strong><p>Paste email addresses separated by commas, spaces, or new lines.</p></div>
      <label>Reviewer email addresses<textarea rows={7} value={rawAddresses} onChange={(event) => setRawAddresses(event.target.value)} placeholder="reviewer@example.org" /></label>
      <p className={addresses.length > 200 ? "is-error" : ""}>{addresses.length} valid addresses{addresses.length > 200 ? " · maximum 200 per batch" : ""}</p>
      <div className="reviewer-invite-dates"><label>Invitation opens<input type="date" required value={opensOn} onChange={(event) => setOpensOn(event.target.value)} /></label><label>Invitation closes<input type="date" required min={opensOn} value={closesOn} onChange={(event) => setClosesOn(event.target.value)} /></label></div>
      <label>Expected reviews per person<input type="number" min="0" max="10000" required value={expectedWork} onChange={(event) => setExpectedWork(event.target.value)} /></label>
      <p className="reviewer-invite-boundary">Each address receives a private response link. Review access begins only after acceptance, onboarding, confidentiality, and a separate assignment.</p>
      {message ? <div className={`reviewer-invite-status ${failed ? "is-failed" : "is-sent"}`} role="status">{failed ? <CircleAlert aria-hidden="true" size={18} /> : <MailCheck aria-hidden="true" size={18} />}<span>{message}</span></div> : null}
    </form>
  </ResponsiveSheet>;
}
